import { useEffect, useRef, useState } from "react";

import { apiClient, type CatalogProduct } from "../../api/client";

interface ProductTableProps {
  products: CatalogProduct[];
  selected: Set<string>;
  loading: boolean;
  selectable: boolean;
  onSelectionChange: (selected: Set<string>) => void;
}

export function ProductTable({
  products,
  selected,
  loading,
  selectable,
  onSelectionChange,
}: ProductTableProps) {
  const [activePreview, setActivePreview] = useState<string | null>(null);
  const allSelected = products.length > 0 && products.every((product) => selected.has(product.id));
  const partlySelected = products.some((product) => selected.has(product.id)) && !allSelected;

  function toggleAll(checked: boolean) {
    onSelectionChange(checked ? new Set(products.map((product) => product.id)) : new Set());
  }

  function toggleOne(productId: string, checked: boolean) {
    const next = new Set(selected);
    if (checked) next.add(productId);
    else next.delete(productId);
    onSelectionChange(next);
  }

  return (
    <div className="catalog-table-frame" aria-busy={loading}>
      <table className="catalog-table">
        <thead>
          <tr>
            <th className="catalog-checkbox-column" scope="col">
              <IndeterminateCheckbox
                ariaLabel="选择当前页全部商品"
                checked={allSelected}
                disabled={!selectable}
                indeterminate={partlySelected}
                onChange={toggleAll}
              />
            </th>
            <th className="catalog-image-column" scope="col">
              图片
            </th>
            <th scope="col">SKU</th>
            <th scope="col">品名</th>
          </tr>
        </thead>
        <tbody>
          {products.map((product) => (
            <tr key={product.id}>
              <td>
                <IndeterminateCheckbox
                  ariaLabel={`选择商品 ${product.sku}`}
                  checked={selected.has(product.id)}
                  disabled={!selectable}
                  indeterminate={false}
                  onChange={(checked) => toggleOne(product.id, checked)}
                />
              </td>
              <td>
                {product.has_image ? (
                  <button
                    aria-label={`预览商品 ${product.sku} 图片`}
                    className="product-image-trigger"
                    onBlur={() => setActivePreview(null)}
                    onClick={() =>
                      setActivePreview((current) => (current === product.id ? null : product.id))
                    }
                    onFocus={() => setActivePreview(product.id)}
                    onMouseEnter={() => setActivePreview(product.id)}
                    onMouseLeave={() => setActivePreview(null)}
                    type="button"
                  >
                    <img
                      alt=""
                      className="product-thumbnail"
                      loading="lazy"
                      src={apiClient.catalogProductImageUrl(product.id, "thumbnail")}
                    />
                    {activePreview === product.id && (
                      <span className="product-preview-popover">
                        <img
                          alt={`${product.name} 大图预览`}
                          src={apiClient.catalogProductImageUrl(product.id, "preview")}
                        />
                      </span>
                    )}
                  </button>
                ) : (
                  <span className="product-no-image">无图</span>
                )}
              </td>
              <td>
                <strong className="catalog-sku">{product.sku}</strong>
              </td>
              <td>{product.name}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {!loading && products.length === 0 && (
        <div className="catalog-empty-state">
          <strong>还没有商品</strong>
          <span>新增商品或导入 CSV 后，商品会显示在这里。</span>
        </div>
      )}
      {loading && <div className="catalog-table-loading">正在加载商品…</div>}
    </div>
  );
}

function IndeterminateCheckbox({
  ariaLabel,
  checked,
  disabled,
  indeterminate,
  onChange,
}: {
  ariaLabel: string;
  checked: boolean;
  disabled: boolean;
  indeterminate: boolean;
  onChange: (checked: boolean) => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (input.current) input.current.indeterminate = indeterminate;
  }, [indeterminate]);
  return (
    <input
      ref={input}
      aria-label={ariaLabel}
      checked={checked}
      disabled={disabled}
      onChange={(event) => onChange(event.target.checked)}
      type="checkbox"
    />
  );
}
